import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { waLink } from "@/lib/whatsapp";
import { getContent } from "@/lib/cms";
import { LOGO_URL } from "@/lib/brand";
import { Search, Heart, ShoppingBag, User, Menu, Leaf, MessageCircle, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export function Header() {
  const { cartCount, wishlist, user, settings } = useApp();
  const content = getContent(settings);
  const h = content.header || {};
  const NAV = h.nav || [];
  const ann = content.announcement || {};
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/shop?search=${encodeURIComponent(q.trim())}`); setOpen(false); }
  };

  return (
    <>
      {ann.enabled && ann.text && (
        <div className="bg-forest text-cream text-center text-xs lg:text-sm py-2 px-4 font-500" data-testid="announcement-bar">{ann.text}</div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-cream/85 border-b border-sage/20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-24 lg:h-32 gap-4">
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              <img src={LOGO_URL} alt={`${h.brand_name || "LIVENEAT"} logo`} className="h-20 lg:h-28 w-auto object-contain" />
            </Link>

            <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-md items-center bg-white rounded-full border border-sage/40 px-4 h-11 focus-within:ring-2 focus-within:ring-sage/50 transition-shadow">
              <Search className="w-4 h-4 text-olive" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="header-search-input"
                placeholder="Search healthy food, products, services..."
                className="flex-1 bg-transparent outline-none px-3 text-sm text-ink placeholder:text-muted-foreground" />
            </form>

            <div className="flex items-center gap-1 lg:gap-2">
              <button onClick={() => { try { localStorage.setItem("ln_guest", "1"); } catch (e) {} nav("/shop"); }} data-testid="guest-login-btn" title="Browse as Guest"
                className="hidden lg:inline-flex items-center gap-2 bg-orange hover:bg-[#e65f00] text-white text-sm font-600 rounded-full px-5 h-10 transition-colors shadow-sm">
                <User className="w-4 h-4" /> Guest Login
              </button>
              {h.show_whatsapp_cta !== false && (
                <a href={waLink(settings.whatsapp_number, "Hi LIVENEAT Organic, I'd like to know more about your products.")}
                  target="_blank" rel="noreferrer" data-testid="header-whatsapp-btn"
                  className="hidden lg:inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-white text-sm font-500 rounded-full px-4 h-10 transition-colors">
                  <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                </a>
              )}
              <Link to={user ? "/account" : "/login"} data-testid="account-link" className="p-2.5 rounded-full hover:bg-sand transition-colors" title="Account">
                <User className="w-5 h-5 text-forest" strokeWidth={1.75} />
              </Link>
              <Link to="/wishlist" data-testid="wishlist-link" className="relative p-2.5 rounded-full hover:bg-sand transition-colors">
                <Heart className="w-5 h-5 text-forest" strokeWidth={1.75} />
                {wishlist.length > 0 && <Badge n={wishlist.length} />}
              </Link>
              <Link to="/cart" data-testid="cart-link" className="relative p-2.5 rounded-full hover:bg-sand transition-colors">
                <ShoppingBag className="w-5 h-5 text-forest" strokeWidth={1.75} />
                {cartCount > 0 && <Badge n={cartCount} />}
              </Link>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button className="lg:hidden p-2.5 rounded-full hover:bg-sand transition-colors" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5 text-forest" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-cream w-[85vw] max-w-sm p-0">
                  <div className="p-6">
                    <form onSubmit={doSearch} className="flex items-center bg-white rounded-full border border-sage/40 px-4 h-11 mb-6">
                      <Search className="w-4 h-4 text-olive" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." data-testid="mobile-search-input"
                        className="flex-1 bg-transparent outline-none px-3 text-sm" />
                    </form>
                    <nav className="flex flex-col gap-1">
                      {NAV.map((n) => (
                        <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                          className="py-3 px-3 rounded-xl text-ink hover:bg-sand font-500 transition-colors">{n.label}</Link>
                      ))}
                    </nav>
                    <a href={waLink(settings.whatsapp_number, "Hi LIVENEAT Organic!")} target="_blank" rel="noreferrer"
                      className="mt-6 inline-flex w-full justify-center items-center gap-2 bg-[#25D366] text-white rounded-full h-11 font-500">
                      <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                    </a>
                    <button onClick={() => { try { localStorage.setItem("ln_guest", "1"); } catch (e) {} setOpen(false); nav("/shop"); }} data-testid="mobile-guest-login-btn"
                      className="mt-3 inline-flex w-full justify-center items-center gap-2 bg-orange text-white rounded-full h-11 font-600">
                      <User className="w-4 h-4" /> Guest Login
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-8 h-12 border-t border-sage/15">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                className="text-sm font-500 text-brown hover:text-terracotta transition-colors relative group">
                {n.label}
                <span className="absolute -bottom-[13px] left-0 w-0 h-[2px] bg-terracotta group-hover:w-full transition-all duration-300" />
              </Link>
            ))}
          </nav>
        </div>
      </header>
    </>
  );
}

function Badge({ n }) {
  return <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-white text-[10px] font-600 w-4.5 h-4.5 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">{n}</span>;
}
